<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    :subtitle="subtitle"
    :section="section"
    has-footer
    title="Additional Identifier"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <!-- autocomplete kind:identifiersOptions -->
        <v-col>
          <v-autocomplete
            v-model="section.kind"
            :items="identifiersOptions"
            label="Identifier*"
            outlined
            :error-messages="getErrorMessages($v.section.kind)"
            @input="$v.section.kind.$touch()"
            @blur="$v.section.kind.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            ref="sectionValue"
            v-model="section.value"
            v-mask="valueMask"
            autocomplete="off"
            :label="valueLabel"
            outlined
            :error-messages="getErrorMessages($v.section.value)"
            @input="$v.section.value.$touch()"
            @blur="$v.section.value.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.note"
            autocomplete="off"
            label="Notes"
            outlined
            :error-messages="getErrorMessages($v.section.note)"
            @input="$v.section.note.$touch()"
            @blur="$v.section.note.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { MASKS, applyMask } from '@/utilities'
import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'
import { rules } from '@/utilities/validation-rules'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'

export default {
  name: 'AdditionalIdentifiersCard',
  components: { GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      identifiersOptions: [
        {
          mask: MASKS.ANUMBER,
          text: 'A#',
          value: 'additional_a_number',
        },
        {
          mask: MASKS.SSNUMBER,
          text: 'SSN',
          value: 'additional_ssn',
        },
        {
          mask: null,
          text: 'Other ID',
          value: 'additional_other_id',
        },
      ],

      section: {},
    }
  },

  validations () {
    return {
      section: {
        kind: { required: helpers.withMessage('Identifier is required', required) },
        note: {},
        value: {
          isAnumber: helpers.withMessage('A-number not valid',
            value => rules.aNumRules(value) === true || this.section.kind !== 'additional_a_number'),

          isSSN: helpers.withMessage('SSN not valid',
            value => rules.ssnRules(value) === true || this.section.kind !== 'additional_ssn'),

          required: helpers.withMessage('required',
            requiredIf(this.section.kind)),
        },
      },
    }
  },

  computed: {
    cardIdentifier () { return `additional-identifiers-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peAdditionalIdentifiersCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeAdditionalIdentifiersCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.valueLabel} ${this.section.value})`
    },

    valueLabel () {
      return this.identifiersOptions.find(i => i.value === this.section.kind)?.text
    },

    valueMask () {
      return this.identifiersOptions.find(i => i.value === this.section.kind)?.mask
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAdditionalIdentifiersCardLock',
      'peParty',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
        this.section.value = applyMask(this.section.value, this.valueMask)
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeAdditionalIdentifiersCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peAdditionalIdentifiersCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peAddictionalIdentifiersCardCreateUndo()
      else {
        return this.peAdditionalIdentifiersCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peAdditionalIdentifiersCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peAdditionalIdentifiersCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onSectionUnlock () {
      return this.setPeAdditionalIdentifiersCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peAddictionalIdentifiersCardCreateUndo',
      'setPeAdditionalIdentifiersCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeAdditionalIdentifiersCard',
      'peAdditionalIdentifiersCardCreate',
      'peAdditionalIdentifiersCardDelete',
      'peAdditionalIdentifiersCardRefresh',
      'peAdditionalIdentifiersCardUpdate',
    ]),
  },
}
</script>
